import ApiService from "../Services/ApiService";
import { getActiveBrand } from "../config/environment.config";
import { AccountStatusType } from "../Enum/AccountStatus";

/**
 * Fiche client du tableau de bord professeur : un seul appel agrège ce que le
 * professeur connecté peut voir d'un de ses clients. Les statistiques ne
 * portent que sur SES cours avec ce client.
 */

export interface ClientDetailIdentity {
  id: number;
  firstName: string;
  lastName: string;
  mail: string;
  phone: string | null;
  /** Adresse par défaut lisible, null si le client n'en a pas. */
  address: string | null;
  /** Date de naissance (Y-m-d) et âge, saisis par le client à son inscription. */
  birthday?: string | null;
  age?: number | null;
  createdAt: string;
  /** Contact d'invitation du prof (relances par e-mail). */
  contactId: number | null;
  contactKey: string | null;
}

export interface ClientDetailChild {
  id: number;
  firstName: string;
  lastName: string;
  /** Niveau scolaire (valeurs de classLevels). */
  class: number | null;
  /** Cours faits avec le professeur connecté. */
  coursesCount: number;
  lastCourseAt: string | null;
}

export interface ClientDetailUrssaf {
  /** Statut lu comme dans la liste des clients (AccountStatus). */
  status: AccountStatusType;
  code: string | null;
  etat: string | null;
  description: string | null;
  isClientValidate: boolean;
  /** Date et heure lues dans la description URSSAF, sans fuseau (comme la liste). */
  activationDate: string | null;
  /** Régime coopérative du professeur (statut lu dans les champs *Scoop). */
  isScoop: boolean;
  hasTiersPrestation: boolean;
}

export interface ClientDetailFees {
  customFeesId: number;
  pricePerHour: number | null;
  feesPerHour: number | null;
  hourlyTtc: number | null;
  isTeachrContact: boolean;
  isArchive: boolean;
}

export interface ClientDetailStats {
  coursesCount: number;
  upcomingCount: number;
  /** Durée totale en DEMI-HEURES. */
  totalDuration: number;
  /** Somme de teachrPrice x durée / 2. */
  amountNet: number;
  /** Somme de (teachrPrice + fees) x durée / 2. */
  amountTtc: number;
  firstCourseAt: string | null;
  lastCourseAt: string | null;
  nextCourseAt: string | null;
  /** Séances (un cours ponctuel en compte une, un cours « plusieurs séances » autant que de lignes). */
  sessionsCount: number;
  /** Durée du cours le plus récent, en DEMI-HEURES. */
  lastDuration: number | null;
  currentMonth: {
    coursesCount: number;
    sessionsCount: number;
    /** En demi-heures. */
    duration: number;
    amountNet: number;
    amountTtc: number;
  };
  /** Demandes de paiement en attente de validation par le client (URSSAF). */
  awaitingValidationCount: number;
  validatedCount: number;
  refusedCount: number;
  /** Demandes rejetées par l'URSSAF à la transmission (codes ERR_*). */
  errorCount: number;
  /** Déclarations en revue pour le déplacement (vérification ou approbation). */
  travelReviewCount: number;
  travelRejectedCount: number;
  /** Cours dont la preuve de déplacement est un abonnement transport du prof. */
  coveredBySubscriptionCount: number;
  /** Tarif horaire du cours le plus récent. */
  lastTeachrPrice: number | null;
  lastFees: number | null;
}

export interface ClientDetailTravelMode {
  mode: string | null;
  label: string;
  distanceKm: number | null;
  isValid: boolean;
  invalidatedAt: string | null;
  invalidationReason: string | null;
}

export interface ClientDetailDepositAttestation {
  id: number;
  courseId: number | null;
  courseLabel: string;
  depositAmount: number;
  /** Y-m-d */
  depositDate: string;
  signedAt: string;
  isValid: boolean;
  invalidationReason: string | null;
  url: string;
}

export interface ClientDetailDeposits {
  validCount: number;
  validTotal: number;
  attestations: ClientDetailDepositAttestation[];
}

export interface ClientDetail {
  client: ClientDetailIdentity;
  children: ClientDetailChild[];
  urssaf: ClientDetailUrssaf;
  fees: ClientDetailFees | null;
  stats: ClientDetailStats;
  travel: {
    travelMode: ClientDetailTravelMode | null;
  };
  /** Attestations sur l'honneur d'acompte signées par ce prof pour ce client. */
  deposits?: ClientDetailDeposits;
}

export default abstract class ClientDetailRepository {
  /**
   * @param fresh contourne le cache de 5 secondes d'ApiService (rechargement
   *              après une action sur la fiche).
   */
  public static getClientDetail(
    teachrId: number,
    clientId: number,
    fresh = false,
  ): Promise<ClientDetail> {
    const params = new URLSearchParams();
    params.set("brand", getActiveBrand());
    if (fresh) params.set("_", String(Date.now()));
    return ApiService.get(
      `/teachrs/v3/teachrs/${teachrId}/clients/${clientId}/detail?${params.toString()}`,
    ) as Promise<ClientDetail>;
  }

  /**
   * Ligne du client au format de la liste « Mes élèves » (all_clients), pour
   * réutiliser telles quelles les actions de la liste (déclarer, actualiser,
   * archiver, supprimer) sur la fiche. Recherche par e-mail puis filtre sur
   * l'identifiant client, dans la liste active ou archivée.
   */
  public static async getClientRow(
    teachrId: number,
    clientId: number,
    mail: string,
    archived: boolean,
    fresh = false,
  ): Promise<any | null> {
    const findIn = async (archive: boolean) => {
      const params = new URLSearchParams();
      params.set("archive", archive ? "1" : "0");
      params.set("page", "0");
      params.set("var", "0");
      params.set("sort", "1");
      params.set("limit", "50");
      params.set("brand", getActiveBrand());
      params.set("validated", "1");
      params.set("search", mail);
      if (fresh) params.set("_", String(Date.now()));
      const response: any = await ApiService.get(
        `/teachrs/v3/teachrs/all_clients/${teachrId}?${params.toString()}`,
      );
      const rows: any[] = Array.isArray(response?.data) ? response.data : [];
      return (
        rows.find((row) => Number(row?.clientId) === Number(clientId)) ?? null
      );
    };
    // L'archivage vient de la fiche tarifaire : on cherche d'abord du bon côté
    return (await findIn(archived)) ?? (await findIn(!archived));
  }
}
